/* eslint-disable no-param-reassign */
import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import clsx from 'clsx';
import { selectSubsidiaries } from 'src/old/actions/subsidiaries/subsidiariesSelectors';
import { makeStyles } from '@mui/styles';
import Page from 'src/shared/components/Page';
import { colors, Grid, TextField, MenuItem, Container, Typography, Button, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { selectCompany } from 'src/old/actions/company/companySelectors';
import { fetchCompany, fetchSubsidiaries } from 'src/old/actions';
import { postEmployee, putEmployee } from 'src/old/actions/employees/employeesActions';
import WithHelpBar from 'src/shared/components/HelpBar/WithHelpBar';
import { showPromptModal, hideModal } from 'src/old/actions/modal/modalActions';
import { MODAL_TYPES } from 'src/shared/components/Modals';
import { fetchBankEntities } from 'src/old/actions/bank/bankActions';
import { selectBankEntities } from 'src/old/actions/bank/bankSelectors';
import { useGetCollectivesQuery } from 'src/shared/api/collectives';
import useForm from '../../shared/utils/Form/useForm';
import generateInputs from './inputsOptions';
import { any } from '../../shared/utils/array';
import SeverityGroups from './SeverityGroups';
import { ErrorIcon } from '../../shared/components/CustomIcons';
import getStateValidatorSchemaByLocale from './getStateValidatorSchemaByLocale';
import getDefaultEmployeeOptionsByLocale from './getDefaultEmployeeOptionsByLocale';
import { parseLocaleNumber } from 'src/shared/hooks/useCurrencyMask';

const useStyles = makeStyles(theme => ({
  root: {
    paddingTop: theme.spacing(4.5),
    paddingBottom: theme.spacing(3)
  },
  title: {
    textAlign: 'left'
  },
  tabs: {
    marginTop: theme.spacing(3)
  },
  divider: {
    backgroundColor: colors.grey[300]
  },
  content: {
    height: '100%',
    borderRadius: 10,
    backgroundColor: '#fff',
    padding: ({ employee }) => (!employee ? theme.spacing(5) : 0)
  },
  helpBarWrapper: {
    marginTop: theme.spacing(4.5),
    padding: theme.spacing(1),
    borderRadius: 10,
    backgroundColor: '#fff'
  },
  ModalInput: {
    borderRadius: 2,
    height: 50,
    backgroundColor: '#f5f9ff'
  },
  buttonDefault: {
    height: 50,
    width: 225,
    borderRadius: 10,
    marginLeft: 'auto',
    marginRight: theme.spacing(1),
    marginTop: theme.spacing(2),
    fontSize: 16,
    fontWeight: 600,
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: 1.25,
    letterSpacing: 0.5,
    textAlign: 'center'
  },
  buttonRowNoTopPadding: {
    paddingTop: '0 !important'
  },
  feedback: {
    margin: theme.spacing(2)
  },
  feedbackContainer: {
    padding: theme.spacing(0.5),
    marginTop: theme.spacing(3)
  },
  warningFormField: {
    '& *': {
      borderColor: `${theme.palette.warning.main} !important`,
      color: `${theme.palette.warning.main} !important`
    }
  }
}));

const processEmployee = (values, locale, isExtraPaymentEnabled) => {
  if (values.salaryType === 'net') {
    values.netSalary = parseLocaleNumber(values.salary).toString();
  } else if (values.salaryType === 'gross') {
    values.grossSalary = parseLocaleNumber(values.salary).toString();
  }

  const fieldsToRemove = ['active', 'counter', 'agreedTyC', 'lastLearnflowUse'];
  for (const field of fieldsToRemove) {
    delete values[field];
  }
  if (!isExtraPaymentEnabled) {
    delete values.collectiveIds;
  }
  values.email = values.email || null;
  return values;
};

const ManageEmployee = ({ employee = null, edit = false, onSubmit = null }) => {
  const subsidiaries = useSelector(selectSubsidiaries);
  const currentCompany = useSelector(selectCompany);
  const bankEntities = useSelector(selectBankEntities);
  const isExtraPaymentEnabled = Boolean(currentCompany?.settings?.extraPayment);
  const { data: collectives } = useGetCollectivesQuery(undefined, { skip: !isExtraPaymentEnabled });
  const history = useHistory();
  const dispatch = useDispatch();
  const [employeeOptions, setEmployeeOptions] = useState(getDefaultEmployeeOptionsByLocale(currentCompany?.locale));

  useEffect(() => {
    if (!edit) {
      setEmployeeOptions(getDefaultEmployeeOptionsByLocale(currentCompany?.locale));
    }
  }, [currentCompany, edit]);
  const [currentSuggestions, setCurrentSuggestions] = useState([]);
  const classes = useStyles();
  const [pending, setPending] = useState(false);
  const { t } = useTranslation(['employee', 'common', 'form']);

  useEffect(() => {
    if (!employee && !edit) {
      Promise.all([
        fetchCompany()(dispatch),
        fetchSubsidiaries()(dispatch),
        fetchBankEntities(currentCompany?.locale)(dispatch)
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSuccess = updateResult => {
    if (!employee) {
      history.push('/list-employees');
    } else {
      const hideModalAndRedirect = route => {
        history.push(route);
        hideModal()(dispatch);
      };

      setPending(false);
      if (updateResult.outstandingPayments) {
        const modalProps = {
          content: () => (
            <>
              <Typography className={classes.modalTitle} variant="h2">
                {t('you just made an employee inactive', { ns: 'employee' })}
              </Typography>
              <Typography className={classes.modalBody} variant="h4">
                {t('it has been detected that there are requested advances during this month', { ns: 'employee' })}{' '}
                {t('would you like to see the liquidation list', {
                  ns: 'employee'
                })}
              </Typography>
            </>
          ),
          onConfirm: () => hideModalAndRedirect('/liquidation-list'),
          closeModal: () => hideModal()(dispatch),
          icon: <ErrorIcon />,
          confirmLabel: t('view the liquidation list', { ns: 'employee' })
        };
        showPromptModal(modalProps, MODAL_TYPES.PROMPT)(dispatch);
      } else {
        hideModalAndRedirect('/list-employees');
      }
    }
  };

  const onFailure = () => {
    setPending(false);
  };

  const addEmployee = async values =>
    postEmployee(
      processEmployee(values, currentCompany?.locale, isExtraPaymentEnabled),
      onSuccess,
      onFailure
    )(dispatch);
  const editEmployee = async values => {
    const updateResult = await putEmployee(
      values.personalId,
      processEmployee(values, currentCompany?.locale, isExtraPaymentEnabled),
      onSuccess,
      onFailure
    )(dispatch);
    if (onSubmit) onSubmit(updateResult);
  };

  const submit = async values => (employee ? editEmployee(values) : addEmployee(values));

  const validatorsSchema = useMemo(() => {
    return getStateValidatorSchemaByLocale(currentCompany?.locale);
  }, [currentCompany]);

  const { fields, errors, suggestions, dirtyFields, handleOnChange, handleOnSubmit, disabledSubmit } = useForm(
    employeeOptions,
    validatorsSchema,
    submit,
    getDefaultEmployeeOptionsByLocale(currentCompany?.locale)
  );

  const inputs = generateInputs(
    fields,
    handleOnChange,
    currentCompany,
    subsidiaries,
    bankEntities,
    collectives ?? [],
    isExtraPaymentEnabled
  );
  useEffect(() => {
    setCurrentSuggestions(Object.entries(suggestions));
  }, [suggestions]);

  const submitLabel = employee ? t('edit employee') : t('add employee');
  useEffect(() => {
    if (employee) {
      setEmployeeOptions(employee);
    }
  }, [employee]);

  return (
    <Page className={classes.root} title={t(submitLabel)}>
      <Container maxWidth="lg">
        <Typography className={classes.title} component="h1" gutterBottom variant="h1">
          {`${submitLabel} ${t('common:on')} ${currentCompany?.name}`}
        </Typography>
        {!employee && (
          <Typography component="h5" gutterBottom variant="h5">
            {t('to add employees one by one, enter their details in the following form below')}
          </Typography>
        )}
        <WithHelpBar hide={edit}>
          <div className={classes.content}>
            <Grid item container spacing={3}>
              {any(inputs) &&
                inputs.map(input => {
                  const {
                    helperText,
                    InputLabelProps = {},
                    InputProps = { className: classes.ModalInput },
                    name,
                    options = {},
                    select = false,
                    selectProps = {},
                    Tag = TextField,
                    type = '',
                    value,
                    variant = 'outlined',
                    md = 6,
                    label,
                    ...rest
                  } = input || {};
                  const maybeSuggestion = Boolean(suggestions[name]) && Boolean(dirtyFields[name]);
                  const maybeError = Boolean(errors[name]) && Boolean(dirtyFields[name]);
                  const feedbackProps = {};
                  if (maybeError) {
                    feedbackProps.error = true;
                    feedbackProps.helperText = errors[name].error;
                  }
                  if (employee && (name === 'personalId' || name === 'documentType')) {
                    feedbackProps.disabled = true;
                  }
                  const warningClass = clsx(maybeSuggestion && classes.warningFormField);

                  return (
                    input && (
                      <Grid item md={md} xs={12} key={name}>
                        <Tag
                          {...feedbackProps}
                          fullWidth
                          InputLabelProps={InputLabelProps}
                          InputProps={InputProps}
                          name={name}
                          key={name}
                          select={select}
                          SelectProps={selectProps}
                          type={type}
                          value={value || ''}
                          variant={variant}
                          className={warningClass}
                          label={t(`form:field.${label}`)}
                          {...rest}
                        >
                          {select &&
                            any(options) &&
                            Object.entries(options).map(([, { value, label, disabled }]) => (
                              <MenuItem key={value} value={value} disabled={disabled}>
                                {t(label)}
                              </MenuItem>
                            ))}
                        </Tag>
                      </Grid>
                    )
                  );
                })}
              <Grid
                item
                xs={12}
                container
                justifyContent="flex-end"
                className={clsx(!isExtraPaymentEnabled && classes.buttonRowNoTopPadding)}
              >
                <Button
                  className={classes.buttonDefault}
                  color="primary"
                  type="submit"
                  variant="contained"
                  onClick={handleOnSubmit}
                  disabled={disabledSubmit || pending}
                >
                  {pending ? <CircularProgress /> : t(submitLabel)}
                </Button>
              </Grid>
            </Grid>
            <div className={classes.feedbackContainer}>
              {any(currentSuggestions) && <SeverityGroups className={classes.feedback} feedback={currentSuggestions} />}
            </div>
          </div>
        </WithHelpBar>
      </Container>
    </Page>
  );
};

ManageEmployee.propTypes = {
  employee: PropTypes.object,
  onSubmit: PropTypes.func,
  edit: PropTypes.bool
};

export default ManageEmployee;
